/**
 * Constructor.io shared client.
 *
 * The single place that talks to Constructor, and the single failure contract.
 * Surfaces never call the SDK directly and never implement their own retry,
 * abort or fallback logic.
 */

/**
 * @typedef {object} ConstructorConfig
 * @property {string} indexKey
 * @property {string|null} userId
 * @property {string} market
 * @property {string} locale
 * @property {string} currency
 * @property {string} searchUrl
 * @property {number} timeoutMs
 * @property {number} maxRetries
 * @property {boolean} designMode
 */

/** @type {ConstructorConfig | null} */
let config = null;

/**
 * Reads the config island once, then memoises.
 * @returns {ConstructorConfig | null}
 */
export function getConfig() {
  if (config) return config;
  const el = document.getElementById('constructor-config');
  if (!el?.textContent) return null;
  try {
    config = JSON.parse(el.textContent);
  } catch {
    return null;
  }
  return config;
}

/**
 * Is this error worth retrying?
 *
 * The previous implementation tested `error.name === 'NetworkError'` and
 * `error.code === 'ECONNABORTED'`. Neither is produced by `fetch` — a network
 * failure is a TypeError and a timeout is an AbortError — so its entire retry
 * budget was unreachable and every transient failure went straight to a
 * fallback. Exported so that behaviour is pinned by a test.
 *
 * @param {any} error
 * @returns {boolean}
 */
export function shouldRetry(error) {
  if (!error) return false;
  if (typeof error.status === 'number') return error.status >= 500;
  if (error instanceof TypeError) return true;
  return error.name === 'AbortError' || error.name === 'TimeoutError';
}

/**
 * Is this a configuration mistake rather than a transient outage?
 *
 * Constructor answers a badly-formed request with a 4xx and a human-readable
 * `message` -- asking a seed-required pod for recommendations without an
 * `item_id` returns `{"message": "item_id: Field required"}`. The SDK surfaces
 * that as a plain `Error`, which is indistinguishable from a network failure
 * unless we look. The difference matters: an outage should degrade quietly to
 * the server-rendered page, but a misconfigured pod should say so, because
 * nobody can fix what they cannot see.
 *
 * @param {any} error
 * @returns {boolean}
 */
export function isConfigError(error) {
  if (!error) return false;
  if (error.name === 'SdkUnavailableError') return false;
  if (error instanceof TypeError) return false;
  if (error.name === 'AbortError' || error.name === 'TimeoutError') return false;
  if (typeof error.status === 'number') return error.status >= 400 && error.status < 500;
  // A rejection carrying a message but no transport signature came from
  // Constructor's own validation, not from the network.
  return typeof error.message === 'string' && error.message.length > 0;
}

/**
 * @param {number} attempt
 * @returns {number}
 */
function backoffMs(attempt) {
  return Math.min(250 * 2 ** attempt, 2000);
}

/** @type {Map<string, AbortController>} */
const inflight = new Map();

/** @type {Map<string, number>} */
const sequence = new Map();

/**
 * Current sequence number for a surface. A caller captures this before
 * awaiting and re-checks it immediately before every DOM write, so a stale
 * response can never overwrite a newer render.
 * @param {string} surface
 * @returns {number}
 */
export function currentSequence(surface) {
  return sequence.get(surface) ?? 0;
}

/** @type {Promise<any> | null} */
let clientPromise = null;

/**
 * Resolves the SDK singleton, or rejects once the deadline passes.
 *
 * Memoised, so one blocked CDN fails every surface fast instead of each one
 * polling independently.
 *
 * @param {number} [giveUpAfterMs] - Defaults to the configured request timeout.
 * @returns {Promise<any>}
 */
function getClient(giveUpAfterMs) {
  if (clientPromise) return clientPromise;

  clientPromise = new Promise((resolve, reject) => {
    const cfg = getConfig();
    if (!cfg?.indexKey) {
      reject(new Error('constructor: missing config or index key'));
      return;
    }

    const deadline = Date.now() + (giveUpAfterMs ?? cfg.timeoutMs ?? 8000);

    // The poll alone costs up to a full tick of latency: the SDK can land at
    // t=210ms and go unnoticed until t=300ms. Listening to the script element
    // removes that dead time; the poll stays as the fallback for the case where
    // the tag already fired load before this module ran.
    const sdkTag = document.querySelector('script[src*="cdn.cnstrc.com"]');

    const attempt = () => {
      const Ctor = /** @type {any} */ (window).ConstructorioClient;
      if (typeof Ctor === 'function') {
        resolve(
          new Ctor({
            apiKey: cfg.indexKey,
            userId: cfg.userId || undefined,
            // Set explicitly rather than relying on defaults: these cards are
            // injected after page load, so the DOM auto-beacon cannot be
            // assumed to pick them up.
            sendTrackingEvents: true,
            eventDispatcher: { waitForBeacon: false },
          })
        );
        return;
      }

      // Give up rather than poll forever. An ad blocker or a CDN outage on
      // cdn.cnstrc.com is a common, not exotic, path: polling forever leaves
      // every surface stuck on skeletons with aria-busy set, no beacon, and no
      // way for the caller's catch to ever run.
      if (Date.now() > deadline) {
        reject(Object.assign(new Error('constructor: SDK unavailable'), { name: 'SdkUnavailableError' }));
        return;
      }

      // Plain setTimeout, not rAF: there is no DOM work here, and rAF is
      // throttled to zero in a background tab, which would stall the deadline.
      setTimeout(attempt, 100);
    };

    sdkTag?.addEventListener('load', attempt, { once: true });
    attempt();
  });

  return clientPromise;
}

/** The interactions the SDK takes as proof of a human, and the flag it keeps once it has seen one. */
const HUMAN_EVENTS = ['scroll', 'resize', 'touchmove', 'mouseover', 'mousemove', 'keydown', 'keypress', 'keyup', 'focus'];
const HUMAN_FLAG = '_constructorio_is_human';

/** @type {Promise<void> | null} */
let humanPromise = null;

/**
 * Resolves once the shopper has interacted with the page.
 *
 * The SDK discards — does not queue — any event sent before it has seen a human
 * interaction; that is its bot filter. A shopper who lands straight on a
 * product page would otherwise lose the page view, which fires on load.
 *
 * Waits on the SDK's own flag rather than on the interaction alone: an
 * interaction before the SDK script has loaded is one the SDK never saw. The
 * flag is per session, so after the first page this resolves at once.
 *
 * @returns {Promise<void>}
 */
function humanSeen() {
  humanPromise ??= new Promise((resolve) => {
    const flagged = () => {
      try {
        return Boolean(sessionStorage.getItem(HUMAN_FLAG));
      } catch {
        // Storage blocked: there is no flag to wait for, so leave it to the SDK.
        return true;
      }
    };
    if (flagged()) {
      resolve();
      return;
    }

    // Checked once the event has finished dispatching, so the SDK's own listener has run.
    const onHuman = () =>
      setTimeout(() => {
        if (!flagged()) return;
        for (const type of HUMAN_EVENTS) window.removeEventListener(type, onHuman, true);
        resolve();
      });
    for (const type of HUMAN_EVENTS) window.addEventListener(type, onHuman, { capture: true, passive: true });
  });
  return humanPromise;
}

/**
 * Sends one behavioural event built by assets/constructor-tracker.js.
 *
 * Fire and forget: tracking must never break, delay or throw into the page that reports it. A
 * blocked SDK or a missing index key just means the event is not sent. The SDK queues events in
 * localStorage, so one reported right before a navigation still goes out on the next page.
 *
 * @param {{ method: string, args: unknown[] } | null} event - Null is a no-op, so callers need not check.
 */
export function trackEvent(event) {
  if (!event) return;

  Promise.all([getClient(), humanSeen()])
    .then(([client]) => {
      const send = client?.tracker?.[event.method];
      if (typeof send !== 'function') throw new Error(`tracker.${event.method} is not a function`);
      const outcome = send.apply(client.tracker, event.args);
      if (outcome instanceof Error) throw outcome;
    })
    .catch((error) => {
      if (getConfig()?.designMode) console.warn(`[constructor] ${event.method}:`, error);
    });
}

/**
 * Runs a Constructor request for a surface, with abort, sequence tracking,
 * timeout and bounded retry.
 *
 * @param {string} surface - e.g. 'recs:pdp-similar-items'
 * @param {(client: any, signal: AbortSignal) => Promise<any>} fn
 * @param {{ timeoutMs?: number, maxRetries?: number }} [budget] - Overrides the configured budget,
 * @returns {Promise<{ data: any, seq: number }>}
 */
export async function query(surface, fn, budget = {}) {
  const cfg = getConfig();
  const maxRetries = budget.maxRetries ?? cfg?.maxRetries ?? 2;
  const timeoutMs = budget.timeoutMs ?? cfg?.timeoutMs ?? 8000;

  inflight.get(surface)?.abort();
  const seq = (sequence.get(surface) ?? 0) + 1;
  sequence.set(surface, seq);

  const client = await getClient();

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (sequence.get(surface) !== seq) {
      throw Object.assign(new Error('superseded'), { superseded: true });
    }

    const controller = new AbortController();
    inflight.set(surface, controller);
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const data = await fn(client, controller.signal);
      return { data, seq };
    } catch (error) {
      if (sequence.get(surface) !== seq) {
        throw Object.assign(new Error('superseded'), { superseded: true });
      }
      if (attempt === maxRetries || !shouldRetry(error)) throw error;
      await new Promise((resolve) => setTimeout(resolve, backoffMs(attempt)));
    } finally {
      clearTimeout(timer);
      if (inflight.get(surface) === controller) inflight.delete(surface);
    }
  }

  throw new Error('constructor: unreachable');
}

/**
 *
 * @param {object} options
 * @param {string} options.handle - The collection the caller is rendering.
 * @param {number} options.minResults - Results the caller needs.
 * @returns {Promise<any> | null}
 */
export function consumeEarlyBrowse({ handle, minResults }) {
  const early = /** @type {any} */ (globalThis).__cnstrcBrowse;
  if (!early?.promise) return null;
  if (early.handle !== handle) return null;
  if (early.requested < minResults) return null;
  return early.promise;
}

/**
 *
 * @param {URL | string} url - The ranked-grid URL the caller is about to fetch.
 * @returns {Promise<string | null> | null}
 */
export function consumeEarlyCards(url) {
  const pending = /** @type {any} */ (globalThis).__cnstrcBrowse?.cards;
  if (!pending) return null;

  const wanted = String(url);
  return pending
    .then(
      /** @param {{ url: string, html: Promise<string>, used?: boolean }[]} groups */
      (groups) => {
        const match = groups.find((group) => !group.used && group.url === wanted);
        if (!match) return null;
        match.used = true;
        return match.html;
      }
    )
    .catch(() => null);
}

/**
 * Pulls the scoped colour rules that transplanted cards depend on.
 *
 * @param {string[]} cssTexts - `textContent` of each <style> in the fetched fragment.
 * @param {Set<string>} classNames - `color-custom-*` classes present on the cards.
 * @returns {string} Deduped CSS, or '' when there is nothing to carry.
 */
export function collectScopedCSS(cssTexts, classNames) {
  if (!classNames?.size) return '';

  const needles = [...classNames].map((name) => `.${name}`);

  /** @type {Map<string, string[]>} body -> selectors */
  const byBody = new Map();

  for (const css of cssTexts) {
    if (!css) continue;

    let selectorStart = 0;
    let depth = 0;
    let bodyStart = -1;

    for (let i = 0; i < css.length; i++) {
      const char = css[i];

      if (char === '{') {
        depth++;
        if (depth === 1) bodyStart = i + 1;
        continue;
      }
      if (char !== '}') continue;

      depth--;
      if (depth !== 0) continue;

      const selector = css.slice(selectorStart, bodyStart - 1).trim();
      const body = css.slice(bodyStart, i).trim();
      selectorStart = i + 1;

      if (selector.startsWith('@')) continue;

      const kept = selector
        .split(',')
        .map((part) => part.trim())
        .filter((part) => needles.some((needle) => classMatches(part, needle)));

      if (!kept.length) continue;

      const existing = byBody.get(body);
      if (existing) existing.push(...kept);
      else byBody.set(body, kept);
    }
  }

  return [...byBody]
    .map(([body, selectors]) => `${[...new Set(selectors)].join(',\n')} {\n${body}\n}`)
    .join('\n');
}

/**
 * Does `selector` use `needle` as a whole class, rather than as the prefix of a
 * longer one? `.color-custom-abc-extra` must not match `.color-custom-abc`.
 *
 * @param {string} selector
 * @param {string} needle - The class name with its leading dot.
 * @returns {boolean}
 */
function classMatches(selector, needle) {
  let from = 0;
  for (;;) {
    const at = selector.indexOf(needle, from);
    if (at === -1) return false;
    const next = selector[at + needle.length];
    if (next === undefined || !/[\w-]/.test(next)) return true;
    from = at + 1;
  }
}

const SORT_ORDERS = new Set(['ascending', 'descending']);

/**
 * Packs a Constructor sort into the single value Horizon's sort control holds.
 *
 * Horizon offers one `name="sort_by"` field, Constructor needs two values, so
 * they travel together as `"<sortBy>:<sortOrder>"` — the same shape the 2024
 * theme used, which keeps shared URLs recognisable.
 *
 * @param {string} sortBy
 * @param {string} sortOrder
 * @returns {string | null} null when either half is missing or invalid.
 */
export function formatSortValue(sortBy, sortOrder) {
  if (!sortBy || !SORT_ORDERS.has(sortOrder)) return null;
  return `${sortBy}:${sortOrder}`;
}

/**
 * Unpacks that value, and refuses anything that is not a Constructor sort.
 *
 * @param {string | null | undefined} value
 * @returns {{ sortBy: string, sortOrder: string } | null}
 */
export function parseSortValue(value) {
  if (typeof value !== 'string') return null;
  const parts = value.split(':');
  if (parts.length !== 2) return null;
  const [sortBy, sortOrder] = parts;
  if (!sortBy || !SORT_ORDERS.has(sortOrder)) return null;
  return { sortBy, sortOrder };
}

/**
 * Namespace for Constructor's own facet inputs.
 *
 * Horizon's inputs carry Shopify GIDs (`gid://shopify/FilterSettingGroup/...`),
 * an opaque grouping id with no product data in it, so they cannot be
 * translated — Constructor's facets are rebuilt as inputs of their own.
 */
const FACET_PREFIX = 'cnstrc.';

/**
 * @param {string} facetName - Constructor's facet name, e.g. 'color'.
 * @returns {string}
 */
export function facetInputName(facetName) {
  return `${FACET_PREFIX}${facetName}`;
}

/**
 * The facet a form field belongs to, or null when the field is not ours.
 * @param {string} inputName
 * @returns {string | null}
 */
export function facetNameFromInput(inputName) {
  if (typeof inputName !== 'string' || !inputName.startsWith(FACET_PREFIX)) return null;
  return inputName.slice(FACET_PREFIX.length) || null;
}

/**
 * Reads form entries into Constructor's `filters` parameter.
 *
 * Multi-select facets become arrays (values OR together within one facet);
 * range facets become a single `"min-max"` string, assembled from the `.min`
 * and `.max` fields. Everything that is not ours is ignored — the form also
 * carries the sort, Shopify's own facets and the search term.
 *
 * @param {Iterable<[string, string]> | null | undefined} entries
 * @returns {Record<string, string[] | string>}
 */
export function collectFilters(entries) {
  /** @type {Record<string, string[]>} */
  const lists = {};
  /** @type {Record<string, { min?: string, max?: string }>} */
  const ranges = {};

  for (const [name, value] of entries ?? []) {
    const facet = facetNameFromInput(name);
    if (!facet || value === '' || value == null) continue;

    const rangeMatch = /^(.*)\.(min|max)$/.exec(facet);
    if (rangeMatch) {
      const [, base, bound] = rangeMatch;
      (ranges[base] ??= {})[/** @type {'min' | 'max'} */ (bound)] = value;
      continue;
    }

    const list = (lists[facet] ??= []);
    if (!list.includes(value)) list.push(value);
  }

  /** @type {Record<string, string[] | string>} */
  const filters = { ...lists };

  for (const [facet, bounds] of Object.entries(ranges)) {
    // Half a range is not a range. Sending one bound produces a malformed pair
    // rather than an open-ended filter.
    if (bounds.min && bounds.max) filters[facet] = `${bounds.min}-${bounds.max}`;
  }

  return filters;
}

/**
 * Pulls `result_id` out of a Constructor payload.
 *
 * The raw REST response carries it at the top level; the SDK's wrapper nests it
 * under `response`. Both paths reach this module, so both must resolve.
 *
 * @param {any} payload
 * @returns {string | null}
 */
export function extractResultId(payload) {
  return payload?.result_id ?? payload?.response?.result_id ?? null;
}

/**
 * Request parameters for one page of a results grid.
 *
 * Browse (`getBrowseResults`) and search (`getSearchResults`) take the same
 * parameters, so a collection page and the search page cannot drift apart in
 * what they ask for.
 *
 * @param {object} request
 * @param {number} request.page
 * @param {number} request.pageSize
 * @param {{ sortBy: string, sortOrder: string } | null} request.sort - null for the index's own relevance order.
 * @param {Record<string, string[] | string>} request.filters
 * @returns {Record<string, unknown>}
 */
export function buildResultsParameters({ page, pageSize, sort, filters }) {
  return {
    resultsPerPage: pageSize,
    page,
    section: 'Products',
    // variation_id, color and image_url: the variation Constructor ranked, which the cards open on
    // (constructor-grid.js showRankedVariation).
    fmtOptions: { fields: ['id', 'variation_id', 'color', 'image_url'] },
    ...(sort ?? {}),
    ...(Object.keys(filters).length ? { filters } : {}),
  };
}

/**
 * The ranked Shopify product ids in a browse or search payload, in rank order.
 * The raw REST response and the SDK's wrapper share this shape.
 *
 * @param {any} data
 * @param {number} limit
 * @returns {string[]}
 */
export function extractRankedIds(data, limit) {
  /** @type {{ data?: { id?: string } }[]} */
  const results = data?.response?.results ?? [];
  return results
    .map((result) => result?.data?.id)
    .filter(/** @returns {id is string} */ (id) => id != null)
    .slice(0, limit);
}

/**
 * The variation Constructor returned for each product in a browse or search payload, by product
 * id. On a colour collection such as Silver it is the matching colour, which the card should show
 * instead of the product's default (usually gold) photo. `color` is that variation's colour option
 * value, used when its id isn't the one a card's swatch carries; `imageUrl` is its photo, for cards
 * that show no swatches (a single colour in stock).
 *
 * @param {any} data
 * @returns {Map<string, { variationId: string, color?: string, imageUrl?: string }>}
 */
export function extractRankedVariations(data) {
  /** @type {Map<string, { variationId: string, color?: string, imageUrl?: string }>} */
  const variations = new Map();
  for (const result of data?.response?.results ?? []) {
    const id = result?.data?.id;
    const variationId = result?.data?.variation_id;
    if (id == null || variationId == null) continue;
    variations.set(String(id), {
      variationId: String(variationId),
      color: result.data.color ?? undefined,
      imageUrl: result.data.image_url ?? undefined,
    });
  }
  return variations;
}

/** The autocomplete sections the EVRY indexes return, by Constructor's own names. */
export const AUTOCOMPLETE_SECTIONS = { products: 'Products', suggestions: 'Search Suggestions' };

/**
 * The search terms and products in an autocomplete payload, in Constructor's order.
 *
 * @param {any} data - `client.autocomplete.getAutocompleteResults` resolves with the raw response.
 * @param {{ products: number, suggestions: number }} limits
 * @returns {{ suggestions: string[], products: { id: string, name: string }[] }}
 */
export function extractAutocomplete(data, limits) {
  const sections = data?.sections ?? data?.response?.sections ?? {};

  /** @type {string[]} */
  const suggestions = [];
  for (const item of sections[AUTOCOMPLETE_SECTIONS.suggestions] ?? []) {
    const term = typeof item?.value === 'string' ? item.value.trim() : '';
    if (term && !suggestions.includes(term)) suggestions.push(term);
  }

  /** @type {{ id: string, name: string }[]} */
  const products = [];
  for (const item of sections[AUTOCOMPLETE_SECTIONS.products] ?? []) {
    const id = item?.data?.id;
    if (id == null || products.some((product) => product.id === String(id))) continue;
    products.push({ id: String(id), name: typeof item.value === 'string' ? item.value : '' });
  }

  return { suggestions: suggestions.slice(0, limits.suggestions), products: products.slice(0, limits.products) };
}

/**
 * Shopify's search attribution: position, search id and search source. The search
 * route appends them to every `product.url` it renders.
 */
const SEARCH_TRACKING_PARAMS = ['_pos', '_sid', '_ss'];

/**
 * Removes Shopify's search-tracking parameters from a card URL.
 *
 * Every Constructor grid fetches its cards through the search route, so without
 * this a collection or recommendation card links to
 * `/products/x?_pos=9&_sid=4c7222490&_ss=r` — a different URL per page view for
 * crawlers, and a Shopify search credited for a click that never came from one.
 * Constructor's own attribution lives on the container and the cards' `data-cnstrc-*`
 * attributes, not in the URL. Anything else in the query (a `variant`) is kept.
 *
 * @param {string} value - A relative or absolute URL.
 * @returns {string} The same URL in the same form, without those parameters.
 */
export function stripSearchTracking(value) {
  if (typeof value !== 'string' || !value.includes('?')) return value;

  let url;
  try {
    url = new URL(value, location.origin);
  } catch {
    return value;
  }

  for (const param of SEARCH_TRACKING_PARAMS) url.searchParams.delete(param);
  const path = `${url.pathname}${url.search}${url.hash}`;
  return /^[a-z][a-z\d+.-]*:/i.test(value) ? `${url.origin}${path}` : path;
}

/** Container marker attribute per surface kind. */
const RESULT_KIND_ATTRIBUTE = {
  browse: 'data-cnstrc-browse',
  search: 'data-cnstrc-search',
  recommendations: 'data-cnstrc-recommendations',
};

/**
 * Builds the container-level beacon attributes for a rendered result set.
 *
 * Without these Constructor receives clicks and conversions but cannot tie them
 * back to the response that ranked the products, so the ranking gets no credit
 * and the model gets no feedback. `data-cnstrc-result-id` is the join key.
 *
 * @see https://docs.constructor.com/docs/integrating-with-constructor-behavioral-tracking-data-driven-event-tracking
 *
 * @param {object} options
 * @param {'browse' | 'search' | 'recommendations'} options.kind
 * @param {number} options.numResults - Emitted even when 0; a zero-result set still reports.
 * @param {string | null} [options.resultId]
 * @param {number | string} [options.page]
 * @param {string} [options.filterName] - Browse only, e.g. 'group_id'.
 * @param {string} [options.filterValue] - Browse only, e.g. the collection handle.
 * @param {string} [options.searchTerm] - Search only.
 * @param {string} [options.podId] - Recommendations only. `response.pod.id`; required by the spec.
 * @param {string} [options.seedItems] - Recommendations only. Comma-separated seed item ids.
 * @returns {Record<string, string>}
 */
export function buildResultAttributes({
  kind,
  numResults,
  resultId,
  page,
  filterName,
  filterValue,
  searchTerm,
  podId,
  seedItems,
}) {
  const marker = RESULT_KIND_ATTRIBUTE[kind];
  if (!marker) throw new Error(`constructor: unknown result kind "${kind}"`);

  /** @type {Record<string, string>} */
  const attrs = { [marker]: '' };

  // Not `if (numResults)` — zero is a meaningful value the spec asks for.
  if (numResults != null && Number.isFinite(Number(numResults))) {
    attrs['data-cnstrc-num-results'] = String(numResults);
  }

  // Everything else is omitted when absent. An attribute holding the literal
  // string "undefined" reports as a real value and poisons the data.
  const optional = {
    'data-cnstrc-result-id': resultId,
    'data-cnstrc-result-page': page,
    'data-cnstrc-filter-name': filterName,
    'data-cnstrc-filter-value': filterValue,
    'data-cnstrc-search-term': searchTerm,
    ...(marker === RESULT_KIND_ATTRIBUTE.recommendations
      ? {
          'data-cnstrc-recommendations-pod-id': podId,
          'data-cnstrc-recommendations-seed-items': seedItems,
        }
      : {}),
  };

  for (const [name, value] of Object.entries(optional)) {
    if (value == null || value === '') continue;
    attrs[name] = String(value);
  }

  return attrs;
}

/** @type {Set<string>} */
const beaconed = new Set();

/**
 * The one fallback contract. Idempotent per surface + stage, and wrapped so a
 * GTM problem can never break rendering.
 *
 * @param {{ surface: string, stage: 'initial' | 'post_success', reason: string, handle?: string }} detail
 */
export function revealFallback(detail) {
  const dedupeKey = `${detail.surface}:${detail.stage}`;
  if (beaconed.has(dedupeKey)) return;
  beaconed.add(dedupeKey);

  try {
    const w = /** @type {any} */ (window);
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: 'constructor_fallback_shown',
      stage: detail.stage,
      reason: detail.reason,
      surface: detail.surface,
      handle: detail.handle || '',
    });
  } catch {
    /* intentionally silent — analytics must never break the page */
  }

  document.dispatchEvent(new CustomEvent('constructor:fallback', { detail, bubbles: true }));
}

/**
 * Reads the search template's section id out of a full-page response, so the
 * next request can ask for that section alone.
 *
 * @param {Document} fragment
 * @returns {string | null}
 */
export function discoverSearchSectionId(fragment) {
  const wrapper = fragment.querySelector('[ref="grid"]')?.closest('.shopify-section');
  const id = wrapper?.id?.replace(/^shopify-section-/, '');
  return id && id.includes('__') ? id : null;
}

const RANKED_GRID_VIEW = 'constructor-cards';

function rankedSearchURL(ids) {
  const cfg = getConfig();
  const url = new URL(cfg?.searchUrl || '/search', location.origin);
  const sorted = [...new Set(ids.map(String))].sort();
  url.searchParams.set('q', sorted.map((id) => `id:${id}`).join(' OR '));
  url.searchParams.set('type', 'product');
  return url;
}

export function buildRankedGridURL(ids) {
  const url = rankedSearchURL(ids);
  url.searchParams.set('view', RANKED_GRID_VIEW);
  return url;
}

export function buildRankedCardsURL(ids, sectionId) {
  const url = rankedSearchURL(ids);
  if (sectionId) url.searchParams.set('section_id', sectionId);
  return url;
}

export function orderByRank(cards, ids) {
  const byId = new Map(cards.map((card) => [card.getAttribute('data-product-id'), card]));
  return ids.map((id) => byId.get(String(id))).filter((card) => card !== undefined);
}
